import { useRef, useState } from "react";
import { ChartCard } from "./chart-card";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { TabsContent } from "@/components/ui/tabs";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import {
	BAR_VALUE_LABEL_RIGHT,
	CATEGORY_Y_AXIS_WIDTH,
	NO_ANIMATION,
	WrappedCategoryTickY,
	chartHeightForRows,
} from "./chart-ticks";
import { Download } from "lucide-react";
import { type StatisticsByApprover } from "@/lib/statistics-api.service";
import {
	StatisticsFilterControls,
	type StatisticsFilterType,
	type StatisticsFilters,
} from "./StatisticsControls";
import { COLORS, downloadChartPng } from "./utils";

interface ApproversTabProps {
	approverStats: StatisticsByApprover[];
	onFilterChange?: (filter: StatisticsFilterType) => void;
	filters?: StatisticsFilters;
	onFiltersChange?: (filters: StatisticsFilters) => void;
}

export default function ApproversTab({
	approverStats,
	onFilterChange,
	filters = {},
	onFiltersChange,
}: ApproversTabProps) {
	const approverRef = useRef<HTMLDivElement>(null);
	const [filterType, setFilterType] = useState<StatisticsFilterType>("total");

	const handleFilterChange = (newFilter: StatisticsFilterType) => {
		setFilterType(newFilter);
		onFilterChange?.(newFilter);
	};

	const getChartData = () => {
		let chartData = [];

		switch (filterType) {
			case "gender_status":
				chartData = approverStats.map((approver) => ({
					approverName: approver.approverName,
					Nam: approver.male,
					Nữ: approver.female,
				}));
				break;
			case "male_only":
				chartData = approverStats.map((approver) => ({
					approverName: approver.approverName,
					Nam: approver.male,
				}));
				break;
			case "female_only":
				chartData = approverStats.map((approver) => ({
					approverName: approver.approverName,
					Nữ: approver.female,
				}));
				break;
			default:
				chartData = approverStats.map((approver) => ({
					approverName: approver.approverName,
					"Số lượng đã tiếp nhận": approver.receivedCount,
				}));
		}

		// Số người tiếp nhận là hữu hạn và nhỏ (một phòng ban), nên vẽ hết —
		// cắt bớt chỉ giấu mất người xếp cuối mà không đổi được gì về độ đọc.
		return chartData;
	};

	return (
		<TabsContent value="approvers">
			<div ref={approverRef} className="space-y-6">
				<div className="flex justify-between items-center">
					<h2 className="text-2xl font-semibold">Thống kê theo người tiếp nhận</h2>
					<Button
						onClick={() => downloadChartPng(approverRef, "thong-ke-theo-nguoi-tiep-nhan")}
						variant="outline"
						size="sm"
						className="flex items-center gap-2"
					>
						<Download className="h-4 w-4" />
						Tải PNG
					</Button>
				</div>

				<StatisticsFilterControls
					filterType={filterType}
					onFilterChange={handleFilterChange}
					showChartLimit={false}
					filters={filters}
					onFiltersChange={onFiltersChange}
					showFilters={true}
				/>

				<ChartCard title="Thống kê theo người tiếp nhận" filename="thong-ke-theo-nguoi-tiep-nhan">
					{/* Thanh NGANG: hạng mục ở đây là tên người, thứ dài nhất trong mọi
					    tab. Ở dạng cột dọc, các tên phải chen nhau trên trục X và
					    hoặc bị cắt hoặc buộc biểu đồ cuộn ngang; nằm ngang thì nhãn
					    chạy cùng chiều với chữ và mỗi người chỉ tốn thêm một hàng. */}
					<ResponsiveContainer width="100%" height={chartHeightForRows(getChartData().length)}>
						<BarChart
							layout="vertical"
							data={getChartData()}
							margin={{ top: 10, right: 48, left: 8, bottom: 10 }}
						>
							<CartesianGrid strokeDasharray="3 3" />
							<XAxis type="number" />
							<YAxis
								type="category"
								dataKey="approverName"
								width={CATEGORY_Y_AXIS_WIDTH}
								tick={<WrappedCategoryTickY />}
								interval={0}
							/>
							<Tooltip />
							{Object.keys(getChartData()[0] || {})
								.filter((key) => key !== "approverName")
								.map((key, index) => (
									<Bar
										{...NO_ANIMATION}
										key={key}
										dataKey={key}
										fill={COLORS[index % COLORS.length]}
										label={BAR_VALUE_LABEL_RIGHT}
									/>
								))}
						</BarChart>
					</ResponsiveContainer>
				</ChartCard>

				{/* Bảng xếp hạng, KHÔNG phải thanh tiến độ như các tab khác.
				    Tiến độ cần một mẫu số — "trong bao nhiêu hồ sơ của người này,
				    bao nhiêu đã tiếp nhận" — mà mẫu số đó không tồn tại ở đây: hồ sơ
				    không được giao cho ai cả, danh tính người tiếp nhận chỉ suy ra
				    được TỪ việc đã tiếp nhận. Nên chỉ còn một con số cho mỗi người,
				    và thứ tự (server trả về đã giảm dần) là thông tin so sánh. */}
				<Card>
					<CardContent className="p-4 md:p-6">
						<div className="mb-3">
							<h3 className="text-base font-semibold md:text-lg">Xếp hạng người tiếp nhận</h3>
							<p className="mt-0.5 text-sm text-muted-foreground">
								{approverStats.length} người tiếp nhận · số hồ sơ đã tiếp nhận
							</p>
						</div>

						{approverStats.length === 0 ? (
							<p className="py-8 text-center text-sm text-muted-foreground">Không có dữ liệu</p>
						) : (
							// Hai cột đọc theo chữ Z (1-2 rồi 3-4) — đúng thứ tự mặc định
							// của CSS grid. Một cột trên màn hình hẹp vì tên người dài.
							<div className="grid grid-cols-1 gap-x-8 md:grid-cols-2">
								{approverStats.map((approver: StatisticsByApprover, index: number) => (
									<div
										key={approver.approverId}
										className="flex items-center gap-2 border-b py-2.5"
									>
										<span className="w-6 flex-shrink-0 text-right text-sm text-muted-foreground">
											{index + 1}.
										</span>
										<div className="min-w-0 flex-1">
											<p className="break-words text-sm font-medium">{approver.approverName}</p>
											<p className="text-xs text-muted-foreground">ID: {approver.approverId}</p>
										</div>
										{/* Bề rộng cố định + căn phải: hai ô số phải thành hai CỘT
										    thẳng hàng dọc qua mọi hàng, nếu không mắt không có mốc
										    nào để dò khi lướt danh sách. */}
										<span className="w-16 flex-shrink-0 text-right text-sm font-semibold text-green-700 dark:text-green-400">
											{approver.receivedCount.toLocaleString()}
										</span>
										<span className="w-28 flex-shrink-0 text-right text-xs text-muted-foreground">
											<span className="text-blue-600 dark:text-blue-400">
												Nam {approver.male.toLocaleString()}
											</span>
											{" · "}
											<span className="text-pink-600 dark:text-pink-400">
												Nữ {approver.female.toLocaleString()}
											</span>
										</span>
									</div>
								))}
							</div>
						)}
					</CardContent>
				</Card>
			</div>
		</TabsContent>
	);
}
